import { disableLoader, enableLoader } from '@context/actions';
import { SharedLinkFetchState } from '@hook/useSharedLinkResource';
import SharedLinkState from '@module/sharedLinkState';
import type { SharedLinkBill } from '@storeData/sharedLink';
import { getBillPrintByOrder, getLanguages } from '@storeData/common';
import {
    BillLanguage,
    buildBillOrder,
    EN_LANG,
    resolveBillLanguages,
    resolveBillRequest
} from '@util/billPrint';
import React, { useEffect, useRef, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';

const applyBillStyling = () => {
    const billContentEle: any = document.getElementById("billContent");
    if (!billContentEle) return;

    const billBodyStyle: any = billContentEle.getElementsByTagName("style");
    if (billBodyStyle[0]?.sheet?.cssRules?.[0]?.style) {
        billBodyStyle[0].sheet.cssRules[0].style.width = 'inherit';
        billBodyStyle[0].sheet.cssRules[0].style.background = 'white';
        billBodyStyle[0].sheet.cssRules[0].style.padding = 'unset';
    }

    const logoImage: any = billContentEle.getElementsByTagName("img");
    const midEle: any = document.getElementById("mid");
    if (midEle) midEle.style.width = "100%";
    const botEle: any = document.getElementById("bot");
    if (botEle) botEle.style.width = "100%";
    if (logoImage[0]) {
        logoImage[0].style.width = '200px';
        logoImage[0].style.height = 'auto';
    }
};

function OrderBillPrintModal({
    orderData,
    initialBill = null,
    onExplore
}: {
    orderData: any;
    initialBill?: SharedLinkBill | null;
    onExplore?: () => void;
}) {

    const wrapperRTef = useRef<any>();
    const storeData = useSelector((state: any) => state.store ? state.store.storeData : null);
    const [languagesList, setLanguagesList] = useState<BillLanguage[]>(
        initialBill?.languages?.length ? initialBill.languages : [EN_LANG]
    );
    const [activeLaguage, setactiveLaguage] = useState<BillLanguage | null>(() => {
        if (!initialBill) return null;
        return initialBill.languages?.find((l) => l.languageId === initialBill.languageId) || EN_LANG;
    });
    const [showList, setshowList] = useState(false)
    const dispatch = useDispatch();
    const [billState, setBillState] = useState<SharedLinkFetchState>(initialBill ? 'ready' : 'loading')
    const [reloadKey, setReloadKey] = useState(0)
    const [languageSelectionKey, setLanguageSelectionKey] = useState(0)
    const { configData } = useSelector((state: any) => state.store ? state.store.storeData : null);
    const basicConfig = configData?.storeConfig?.basicConfig
    const store = useSelector((state: any) => state);
    const languageData: any = store.languageKeywords

    // The bill arrives as a document from the print service, so it is injected rather than rendered.
    const showBillHtml = (html: string) => {
        if (!wrapperRTef.current) return false;
        wrapperRTef.current.innerHTML = html;
        setTimeout(applyBillStyling);
        return true;
    };

    useEffect(() => {
        if (!initialBill) return;
        showBillHtml(initialBill.html);
        dispatch(disableLoader());
    }, [initialBill]);

    useEffect(() => {
        // The server already resolved the languages alongside the prerendered bill.
        if (initialBill) return;

        getLanguages(storeData.tenantId, storeData.storeId).then((res: any) => {
            const { languagesList: list, activeLanguage } = resolveBillLanguages(res);
            setLanguagesList(list);
            setactiveLaguage(activeLanguage);
        }).catch((error) => {
            console.error("Invoice language error:", error);
            setactiveLaguage(EN_LANG);
        })
    }, [storeData, initialBill])


    const getBillPrintData = (billOrder: any, activeLaguage: BillLanguage) => {
        setBillState('loading');
        const { languageCode, combinePrintBill } = resolveBillRequest(activeLaguage, basicConfig);
        getBillPrintByOrder(billOrder, languageCode, combinePrintBill).then((res: any) => {
            if (res?.data && showBillHtml(res.data)) {
                setBillState('ready')
            } else {
                setBillState('notFound')
            }
            dispatch(disableLoader())
        })
            .catch((error) => {
                console.error("Bill print error:", error);
                setBillState('error')
                dispatch(disableLoader())
            })
    }

    useEffect(() => {
        if (!activeLaguage) return;
        // Keep the server-rendered bill through React Strict Mode's repeated development effects.
        // Only a user language change or explicit retry should replace it with a client request.
        if (initialBill && reloadKey === 0 && languageSelectionKey === 0) return;

        dispatch(enableLoader())
        buildBillOrder(orderData, storeData.categories, languagesList).then((billOrder: any) => {
            getBillPrintData(billOrder, activeLaguage)
        }).catch((error) => {
            console.error("Bill item preparation error:", error);
            setBillState('error');
            dispatch(disableLoader());
        });
    }, [activeLaguage, reloadKey, languageSelectionKey])

    const onSelectLanguage = (e: any, languageData: BillLanguage) => {
        setshowList(false)
        setactiveLaguage(languageData)
        setLanguageSelectionKey((key) => key + 1)
        e.stopPropagation()
    }

    return (
        <div className='orderBillPrintModal' >
            <SharedLinkState
                state={billState}
                onRetry={() => setReloadKey((key) => key + 1)}
                loadingText="Preparing your invoice..."
                notFoundText={languageData['Order data not found'] ? languageData['Order data not found'] : 'Order data not found'}
                errorText="We couldn't prepare your invoice. Please check your connection and try again."
                onSecondaryAction={onExplore}
                secondaryActionText="Explore more services"
            />
            {billState === 'ready' && <>
                {languagesList.length > 1 && <>
                    <div className='selected-language-wrap' onClick={() => setshowList(true)}>
                        <div className='selected-language'>
                            {activeLaguage.displayName}
                        </div>
                        <div className="searched-item-list-wrap"
                            style={{ height: showList ? 'auto' : '0', padding: showList ? '8px' : '0' }} onClick={() => { }} >
                            <div className="items-list">
                                {languagesList?.map((language) => {
                                    return <React.Fragment key={language.languageId}>
                                        <div className="expert-details hover" onClick={(e: any) => onSelectLanguage(e, language)}>
                                            <div className="expert-name">{language.displayName}</div>
                                        </div>
                                    </React.Fragment>
                                })}
                            </div>
                        </div>
                    </div>
                </>}
            </>}
            <div className='billContent' id="billContent" ref={wrapperRTef} style={{ display: billState === 'ready' ? 'flex' : 'none' }}></div>
        </div>
    )
}

export default OrderBillPrintModal
